import type { UseFormBlockOptions, UseFormBlockReturn } from '#layers/blocks/types/form-block';
import type { AnyItem, GlobalFieldId, GlobalPrimitiveFieldId } from '#layers/data-model/public';
import type { RStoreFormObject } from '#layers/forms/public';
import { createFormObject } from '@rstore/vue';
import { computed, toValue, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { CrudAction, useAuthorization, useEntitlementNotice, usePermissionsStore, useProcessedFieldSelection } from '#layers/access/public';
import { useBlockContext } from '#layers/block-engine/public';
import { useDetailPageContext } from '#layers/blocks/composables/block-context/use-detail-page-context';
import { useFieldSelectionForFieldIds } from '#layers/blocks/composables/use-field-selection-for-field-ids';
import { isQueryCapabilityAvailable, narrowFieldSelectionToOutput, supportedSurface, useQueryApi, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { FormWriteContext, provideFormBlockContext, useRStoreFormObject } from '#layers/forms/public';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { useEditGuard } from '#layers/ui/public';
import { useFormBlockComputedState } from './use-form-block-computed-state';
import { useFormBlockOverflowActions } from './use-form-block-overflow-actions';
import { useFormBlockSaveHandlers } from './use-form-block-save-handlers';
import { useFormBlockSubmit } from './use-form-block-submit';

/**
 * Master orchestrator composable for Form.block.vue.
 * Composes all form-block sub-composables and returns a unified API.
 */
export function useFormBlock(options: UseFormBlockOptions): UseFormBlockReturn {
	const { t } = useI18n();
	const formId = useId();
	const workspaceHandle = useWorkspaceRuntimeHandle();
	const store = workspaceHandle.store;
	const { getCollection } = useWorkspaceSchemaStore();
	const detailPageContext = useDetailPageContext();
	const { emitOnRootBlock } = useBlockContext();
	const { can } = useAuthorization();
	const { resourceForCollection, descriptorHasAction } = usePermissionsStore();
	const unboundFormObject = createUnboundFormObject();

	const {
		finalCollectionId,
		collectionData,
		isDetailPageForm,
		parsedFinalItemId,
		computedFields,
		getDefaultValues,
		getFieldOverride,
	} = useFormBlockComputedState({
		collectionId: options.collectionId,
		itemId: options.itemId,
		fields: options.fields,
		detailPageContext,
		getCollection,
	});
	const formFieldSelection = useFieldSelectionForFieldIds(
		collectionData,
		computed(() => computedFields.value.map((field) => field.id)),
	);
	const { selection: processedFormFieldSelection } = useProcessedFieldSelection({
		collection: collectionData,
		selection: formFieldSelection,
	});

	const queryApi = useQueryApi(collectionData);

	// A drawer opened from a relation writes through its owner's nested operation; any other form
	// writes through its own collection's create or by-key update.
	const writeContext = computed(() => detailPageContext.writeContext
		?? FormWriteContext.root(collectionData.value?.id, queryApi.api.value, parsedFinalItemId.value));
	const isCreate = computed(() => writeContext.value.isCreate);
	const isUpdate = computed(() => !isCreate.value || !!detailPageContext.itemEdits);
	const formDisabled = computed(() => !!finalCollectionId.value && !writeContext.value.available);

	const readOne = queryApi.surface((api) => api.readOne());
	const isUnreadableItem = computed(() => parsedFinalItemId.value !== null && !isQueryCapabilityAvailable(readOne.value));
	const loadSelection = computed(() => {
		const collection = collectionData.value;
		const selection = processedFormFieldSelection.value;
		const output = supportedSurface(readOne.value)?.output();
		return collection && selection && output ? narrowFieldSelectionToOutput(collection, selection, output) : selection;
	});

	const {
		formObject,
		isLoading,
		initError: loadError,
		hasChanges: formHasChanges,
	} = useRStoreFormObject(
		() => collectionData.value && !isUnreadableItem.value ? store.$collection(collectionData.value.apiName) : undefined,
		() => parsedFinalItemId.value,
		{
			// Only prefill fields the create operation accepts.
			defaultValues: () => {
				const defaults = getDefaultValues();
				const context = writeContext.value;
				return Object.fromEntries((collectionData.value?.primitiveFields ?? [])
					.filter((field) => context.allowsPrimitiveField(field.id) && Object.hasOwn(defaults, field.apiName))
					.map((field) => [field.apiName, defaults[field.apiName]]));
			},
			selection: loadSelection,
			resetOnSuccess: () => toValue(options.resetOnCreate),
			initialEdits: () => detailPageContext.itemEdits,
			onError: () => toValue(options.blockInEditor) ?? false,
		},
	);

	const initError = computed(() => isUnreadableItem.value ? new Error(t('blocks.form.item_unreadable')) : loadError.value);

	const isUnboundForm = computed(() => !finalCollectionId.value);
	const effectiveFormObject = computed<RStoreFormObject<AnyItem> | null>(() => {
		if (isUnboundForm.value) {
			return unboundFormObject;
		}

		return formObject.value;
	});

	const {
		saveHandlerErrors,
		onSave,
		captureSaveHandlers,
		reportHasChanges,
		hasExternalChanges,
		clearSaveHandlerErrors,
	} = useFormBlockSaveHandlers();

	// Combined changes: form changes OR external reporters
	const hasChanges = computed(() => formHasChanges.value || hasExternalChanges());
	const effectiveHasChanges = computed(() => {
		if (isUnboundForm.value) {
			return (unboundFormObject.$hasChanges?.() ?? false) || hasExternalChanges();
		}

		return hasChanges.value;
	});

	const { onSubmit, formOnlySubmitDisabled, submitError, submitted } = useFormBlockSubmit({
		formObject: effectiveFormObject,
		collectionData,
		finalCollectionId,
		isCreate,
		writeContext,
		itemId: parsedFinalItemId,
		isDetailPageForm,
		detailPageContext,
		captureSaveHandlers,
		saveHandlerErrors,
		clearSaveHandlerErrors,
		emitOnRootBlock,
		onSuccess: options.onSuccess,
		onSubmit: options.onSubmit,
		hasExternalChanges,
		runOperation: workspaceHandle.run,
	});

	// The entitlement action this form represents; flips create -> update after the first save.
	const formAction = computed<'create' | 'update'>(() => isCreate.value ? CrudAction.Create : CrudAction.Update);

	// The resource covering this form's action, if the descriptor gates this collection for the
	// create/update action. Coverage is action-level (mirrors authorize): a collection covered for
	// a different action is not covered here. Covered actions have no per-field entitlement, so a
	// truthy resource means "gate the whole form by entitlement, all-or-nothing".
	const entitlementResource = computed(() => {
		const collectionId = finalCollectionId.value;
		if (!collectionId) return;

		const resource = resourceForCollection(collectionId);
		return resource && descriptorHasAction(resource, formAction.value) ? resource : undefined;
	});

	const { notice: entitlementNotice } = useEntitlementNotice({
		resource: () => entitlementResource.value?.name ?? '',
		action: formAction,
		collectionId: () => finalCollectionId.value ?? undefined,
		scope: 'row',
	});

	// Mirrors backend enforcement: covered collections gate by entitlement (carrying this item's
	// id for scoped actions); everything else keeps the item-rule create/update check.
	const canSubmitForm = computed(() => {
		const collection = collectionData.value;
		if (!collection) return true;
		if (formDisabled.value) return false;
		return can({ collection, action: formAction.value, id: parsedFinalItemId.value ?? undefined });
	});

	/** A primitive field the form's operation does not accept. Relations gate their own actions. */
	function isFieldUnsupported(fieldId: GlobalFieldId): boolean {
		if (!collectionData.value?.primitiveFields.some((field) => field.id === fieldId)) return false;
		return !writeContext.value.allowsPrimitiveField(fieldId as GlobalPrimitiveFieldId);
	}

	function isFieldDisabled(fieldId: GlobalFieldId): boolean {
		const collection = collectionData.value;
		if (!collection) return false;
		if (writeContext.value.isView || isFieldUnsupported(fieldId)) return true;

		// Covered collections have no per-field entitlement: a field is editable iff the item is
		// (mirrors canSubmitForm). Non-covered collections keep per-field field rules.
		if (entitlementResource.value) return !canSubmitForm.value;

		return !can({ collection, action: formAction.value, fields: [fieldId] });
	}

	function getFieldDisabledNotice(fieldId: GlobalFieldId): string | undefined {
		// A view disables every field for the same reason, so no field needs its own notice.
		if (!isFieldDisabled(fieldId) || writeContext.value.isView) return undefined;
		if (isFieldUnsupported(fieldId)) return t(isCreate.value ? 'blocks.form.field_unsupported.create' : 'blocks.form.field_unsupported.update');
		return entitlementResource.value ? entitlementNotice.value : t('permissions.no_field_access');
	}

	const showSubmit = computed(() => !writeContext.value.isView);

	// Submit is never disabled in create mode (always allow creating)
	// In update mode, disabled if form has no changes AND no external changes
	const submitDisabled = computed(() => {
		if (!canSubmitForm.value) return true;
		if (isCreate.value) return false;
		return formOnlySubmitDisabled.value && !hasExternalChanges();
	});

	const { overflowActions } = useFormBlockOverflowActions({
		isCreate,
		collectionData,
		parsedFinalItemId,
		detailPageContext,
	});

	// Only activate guard when not in editor preview AND has unsaved changes AND not just submitted
	useEditGuard(() => !toValue(options.blockInEditor) && effectiveHasChanges.value && !submitted.value);

	provideFormBlockContext({
		state: effectiveFormObject,
		collection: collectionData,
		itemKey: parsedFinalItemId,
		isCreate,
		writeContext,
		onSave,
		reportHasChanges,
	});

	return {
		formObject: effectiveFormObject,
		isCreate,
		isUpdate,
		isLoading,
		initError,
		hasChanges: effectiveHasChanges,
		collectionData,
		finalCollectionId,
		currentItemId: parsedFinalItemId,
		computedFields,
		isDetailPageForm,
		onSubmit,
		showSubmit,
		submitDisabled,
		submitError,
		saveHandlerErrors,
		submitted,
		onSave,
		reportHasChanges,
		overflowActions,
		getFieldOverride,
		formId,
		formDisabled,
		isFieldDisabled,
		getFieldDisabledNotice,
	};
}

/**
 * Creates a local rstore form object for blocks that are not bound to a collection.
 */
function createUnboundFormObject(): RStoreFormObject<AnyItem> {
	return createFormObject<AnyItem, any, Record<string, never>, AnyItem>({
		submit: async (data) => data as AnyItem,
		validateOnSubmit: false,
		resetOnSuccess: false,
	}) as RStoreFormObject<AnyItem>;
}
